/**
 * The employee's own attendance for one local date.
 *
 * `GET /api/v1/attendance?date_from=D&date_to=D`, with no `employee_id`: step 5 of
 * the 02 §11 chain resolves the caller to themselves, and an omitted filter *is* the
 * self scope (`api/paths.ts`).
 *
 * ── The completeness is not a footnote ─────────────────────────────────────
 *
 * API_CONTRACT §8.3: *"Con số attendance không kèm completeness là con số nói dối"*
 * — an attendance figure without its completeness is a figure that lies. So the
 * three completeness facts each get their own rendering, and they are not merged:
 *
 *   `missing_agent_data` + nothing observed  →  `AgentOffline`, **instead of** the
 *                                               figures. The component has no
 *                                               numeric prop, so this case cannot
 *                                               render as "0 giờ" even by accident.
 *   `missing_agent_data` or `score < 1`      →  `PartialData` **beside** the
 *                                               figures, naming what is excluded
 *                                               from them in minutes.
 *   `unsynced_data`                          →  `DataDelayed source='processing'`:
 *                                               the day is calculated and events
 *                                               behind it are still landing.
 *
 * The first two are different sentences about the same flag and the difference is
 * whether there is a figure to qualify at all. `PRODUCT.md` §4's seventh row is the
 * whole reason: an employee who did not work and an employee whose agent died are
 * different people.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatDay, formatInstant } from '../../i18n/datetime';
import { attendanceDayPath } from '../../api/paths';
import type { AttendanceDay, AttendanceListResponse } from '../../api/types';
import { useApiQuery } from '../../api/useApiQuery';
import { AgentOffline } from '../../states/AgentOffline';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { DataDelayed } from '../../states/DataDelayed';
import { EmptyState } from '../../states/EmptyState';
import { PartialData } from '../../states/PartialData';
import { Skeleton } from '../../states/Skeleton';
import { formatDurationMinutes } from '../../timer/elapsed';
import { readDuration, readMetric, REASON_KEY, type MetricReading } from '../../attendance/status';

export interface AttendanceCardProps {
  readonly date: string;
  readonly timezone: string;
  /** Opens the correction form. Offered on every day — `web/shapes/my-activity.md` §8. */
  readonly onCorrect: () => void;
  readonly reloadToken?: number;
}

interface Figure {
  readonly key: string;
  readonly labelKey:
    | 'attendance.online'
    | 'attendance.active'
    | 'attendance.idle'
    | 'attendance.break'
    | 'attendance.approvedOffline'
    | 'attendance.overtime';
  /**
   * What the field says — **never a raw number**. `formatDurationMinutes` clamps a
   * non-finite input to zero, so handing it the `null` the server sends for a metric
   * it declined to publish printed "0 ph" where the honest answer is a sentence.
   * `readMetric` / `readDuration` keep the three cases apart; D-22 is why.
   */
  readonly reading: MetricReading;
  readonly help?: 'attendance.idle.help';
}

function figuresOf(day: AttendanceDay): readonly Figure[] {
  const dayReason = day.reason ?? null;
  return [
    {
      key: 'online',
      labelKey: 'attendance.online',
      reading: readDuration(day.online_minutes, dayReason),
    },
    {
      key: 'active',
      labelKey: 'attendance.active',
      reading: readDuration(day.active_minutes, dayReason),
    },
    {
      key: 'idle',
      labelKey: 'attendance.idle',
      reading: readDuration(day.idle_minutes, dayReason),
      help: 'attendance.idle.help',
    },
    {
      key: 'break',
      labelKey: 'attendance.break',
      reading: readDuration(day.break_minutes, dayReason),
    },
    {
      key: 'approved-offline',
      labelKey: 'attendance.approvedOffline',
      reading: readDuration(day.approved_offline_minutes, dayReason),
    },
    {
      // The one figure here that is a shift-relative **metric**, so its absence has
      // a reason of its own: `overtime_not_credited_on_incomplete_data` on a holed
      // day, `overtime_requires_manual_claim` on a `manual_only` shift. Both are
      // pointers at the correction workflow, and both used to read as "0 ph".
      key: 'overtime',
      labelKey: 'attendance.overtime',
      reading: readMetric(day.overtime_minutes, day.withheld, 'overtime_minutes'),
    },
  ];
}

export function AttendanceCard({
  date,
  timezone,
  onCorrect,
  reloadToken = 0,
}: AttendanceCardProps): ReactNode {
  const { t, locale } = useI18n();
  const { status, data, error, reload } = useApiQuery<AttendanceListResponse>(
    attendanceDayPath(date),
    reloadToken,
  );

  const day = data?.data[0] ?? null;
  const durations = { hour: t('common.hourShort'), minute: t('common.minuteShort') };

  const correctButton = (
    <button
      type="button"
      className="wp-button"
      onClick={onCorrect}
      data-testid="attendance-correct"
    >
      {t('attendance.correct')}
    </button>
  );

  return (
    <section className="wp-section" aria-labelledby="attendance-heading">
      <h2 className="wp-section__title" id="attendance-heading">
        {t('attendance.heading', {
          date: formatDay(date, locale, { day: 'numeric', month: 'long', year: 'numeric' }),
        })}
      </h2>

      {status === 'loading' ? (
        <Skeleton
          rows={3}
          variant="block"
          label={t('attendance.loading')}
          testId="attendance-skeleton"
        />
      ) : null}

      {status === 'error' && error !== null ? (
        <ApiErrorNote
          error={error}
          onRetry={reload}
          subject={t('attendance.subject')}
          testId="attendance-error"
        />
      ) : null}

      {status === 'ok' && day === null ? (
        // No row for this date. Not "you did nothing": Task 26 writes no session row
        // for an employee with no events, which is exactly the day a correction is
        // for — so the empty state carries the correction path rather than a shrug.
        <EmptyState
          title={t('attendance.empty.title')}
          description={t('attendance.empty.body')}
          action={correctButton}
          testId="attendance-empty"
        />
      ) : null}

      {day === null ? null : (
        <div className="wp-card" data-testid="attendance-card">
          <p className="wp-small wp-muted">
            {t('attendance.window', {
              first:
                day.first_activity_at === null || day.first_activity_at === undefined
                  ? t('attendance.noFirst')
                  : formatInstant(day.first_activity_at, locale, timezone),
              last:
                day.last_activity_at === null || day.last_activity_at === undefined
                  ? t('attendance.noLast')
                  : formatInstant(day.last_activity_at, locale, timezone),
            })}
          </p>

          {/* `AgentOffline` replaces the figures rather than annotating them. There is
              no number to qualify: nothing was observed, and the reason is that the
              agent sent nothing — which is a different fact from a zero. */}
          {day.completeness.missing_agent_data && (day.online_minutes ?? 0) === 0 ? (
            <AgentOffline
              liveness="offline"
              lastHeartbeatAt={null}
              heartbeatUnavailable
              timezone={timezone}
              deviceLabel={t('attendance.agentDevice')}
              actions={correctButton}
              testId="attendance-agent-offline"
            />
          ) : (
            <>
              {/* Six figures, each printed with its own label and — when there is no
                  number — with the sentence that says why, at sentence size rather
                  than at figure size. A withheld reading set in 19px tabular numerals
                  would read as a measurement. */}
              <dl className="wp-figures wp-myfigures" data-testid="attendance-figures">
                {figuresOf(day).map((figure) => (
                  <div key={figure.key} className="wp-figures__item wp-myfigure">
                    <dt
                      className="wp-label"
                      {...(figure.help === undefined ? {} : { title: t(figure.help) })}
                    >
                      {t(figure.labelKey)}
                    </dt>
                    <dd
                      className={
                        figure.reading.kind === 'measured'
                          ? 'wp-myfigure__value'
                          : 'wp-myfigure__absence wp-small'
                      }
                      data-testid={`attendance-${figure.key}`}
                    >
                      {figure.reading.kind === 'measured'
                        ? formatDurationMinutes(figure.reading.minutes, durations)
                        : figure.reading.kind === 'withheld'
                          ? t(REASON_KEY[figure.reading.reason])
                          : t('attendance.notReported')}
                    </dd>
                  </div>
                ))}
              </dl>

              {day.completeness.score >= 1 && !day.completeness.missing_agent_data ? null : (
                <PartialData
                  excludes={
                    (day.missing_minutes ?? 0) > 0
                      ? t('attendance.excludes.minutes', {
                          minutes: formatDurationMinutes(day.missing_minutes ?? 0, durations),
                        })
                      : t('attendance.excludes.agent')
                  }
                  score={day.completeness.score}
                  deviceCount={day.completeness.device_count ?? null}
                  actions={correctButton}
                  testId="attendance-partial"
                />
              )}
            </>
          )}

          {day.completeness.unsynced_data ? (
            <DataDelayed
              source="processing"
              since={day.last_activity_at ?? null}
              timezone={timezone}
              detail={t('attendance.unsynced')}
              testId="attendance-unsynced"
            />
          ) : null}

          {/* `adjustments_applied` is in `openapi.yaml` and in API_CONTRACT §8.3 and is
              **not sent** — the string appears nowhere in `src/`. So the fact is
              reported through the field that is published: `approved_offline_minutes`
              is `docs/DATA_MODEL.md` §5.12's sum of the day's approved explanations,
              so a positive value means at least one correction was approved. No count
              is claimed, because none is available. */}
          {(day.approved_offline_minutes ?? 0) > 0 ? (
            <p className="wp-small wp-muted" data-testid="attendance-adjustments">
              {t('attendance.adjustments', {
                minutes: formatDurationMinutes(day.approved_offline_minutes ?? 0, durations),
              })}
            </p>
          ) : null}

          {day.completeness.missing_agent_data && (day.online_minutes ?? 0) === 0 ? null : (
            <div className="wp-myactions">{correctButton}</div>
          )}
        </div>
      )}
    </section>
  );
}
